/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : validateFlow.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
export function validateFlow(nodes, edges) {
  const errors = [];
  const invalidNodeIds = new Set();
  const invalidEdgeIds = new Set();

  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const outgoing = {};
  const incoming = {};

  edges.forEach((e) => {
    outgoing[e.source] ||= [];
    incoming[e.target] ||= [];
    outgoing[e.source].push(e);
    incoming[e.target].push(e);
  });

  /* ----------------------------------
     Rule 1: Question answers connected
  ----------------------------------- */
  nodes
    .filter((n) => n.type === "question")
    .forEach((q) => {
      q.data.answers.forEach((a) => {
        const connected = edges.some(
          (e) => e.source === q.id && e.sourceHandle === a.id,
        );
        if (!connected) {
          errors.push({
            level: "error",
            message: `Question "${q.data.questionText || "Untitled"}": option "${a.label}" is not connected`,
            nodeId: q.id,
          });
          invalidNodeIds.add(q.id);
        }
      });
    });

  /* ----------------------------------
     Rule 2: End node has outcome
  ----------------------------------- */
  nodes
    .filter((n) => n.type === "end")
    .forEach((e) => {
      if (!e.data?.outcome?.value) {
        errors.push({
          level: "error",
          message: "End node missing outcome",
          nodeId: e.id,
        });
        invalidNodeIds.add(e.id);
      }
    });

  /* ----------------------------------
     Rule 3: All paths end in End
  ----------------------------------- */
  const start = nodes.find((n) => n.type === "start");
  if (!start) {
    errors.push({
      level: "error",
      message: "Missing Start node",
    });
    return { errors, invalidNodeIds: [...invalidNodeIds], invalidEdgeIds: [] };
  }

  const visited = new Set();

  function dfs(nodeId, path = []) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap[nodeId];
    const outs = outgoing[nodeId] || [];



    if (outs.length === 0 && node?.type !== "end") {
      errors.push({
        level: "error",
        message: `Path ends at "${node?.type}" node without End`,
        nodeId: nodeId,
      });
      invalidNodeIds.add(nodeId);
      // mark incoming edges as invalid
      (incoming[nodeId] || []).forEach((e) => invalidEdgeIds.add(e.id));
    }

    outs.forEach((e) => dfs(e.target, [...path, nodeId]));
  }

  dfs(start.id);

  /* ----------------------------------
     Rule 4: Start reaches an End
  ----------------------------------- */
  const reachesEnd = nodes
    .filter((n) => n.type === "end")
    .some((end) => incoming[end.id]?.length);

  if (!reachesEnd) {
    errors.push({
      level: "error",
      message: "No path from Start reaches an End node",
      nodeId: start.id,
    });
    invalidNodeIds.add(start.id);
  }

  return {
    errors,
    invalidNodeIds: [...invalidNodeIds],
    invalidEdgeIds: [...invalidEdgeIds],
  };
}
